import { useState, useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { fetchBody, sendEmail } from '../services/MessagesService';
import { useAliases } from '../../aliases/hooks/useAliases';
import { htmlToPlainText } from '../../../utils/htmlSanitizer';
import { Message } from '../types';
import { useGenerateTestMessage } from './useGenerateTestMessage';

interface ComposeEmailFormValues {
    aliasId: string;
    to: string;
    subject: string;
    htmlBody: string;
    useHtml: boolean;
}

export const useComposeEmail = (
    opened: boolean,
    aliasId?: string,
    replyTo?: Message,
    replyAll?: boolean,
    forwardMessage?: Message
) => {
    const { items: aliases } = useAliases();
    const queryClient = useQueryClient();
    const { generateTestMessage } = useGenerateTestMessage();
    const [initialValues, setInitialValues] = useState<ComposeEmailFormValues>({
        aliasId: aliasId || '',
        to: '',
        subject: '',
        htmlBody: '',
        useHtml: true
    });

    const aliasOptions = aliases.map((alias) => ({
        value: alias.id,
        label: alias.name || alias.id,
    }));

    /**
     * FNP-1532: quoted text comes from the API, not from S3.
     *
     * The old version built an S3 path here — including a
     * `htmlObjectKey.replace('/html', '/text')` string hack that guessed at a
     * sibling artifact — and fetched it with the browser's Cognito credentials.
     * That path is gone: the bucket is in another account now, and the client
     * has no business choosing artifacts (FNP-877).
     *
     * ⚠️ THE SERVER PICKS THE ARTIFACT, AND FOR HTML-ONLY MAIL THAT IS `html`.
     * SES writes a 0-byte `text` object for an HTML-only message, so the server's
     * `text -> parsed -> html` preference falls through to markup — and HTML-only
     * is the common inbound shape. Quoting that raw would paste a full HTML
     * document into the draft. Hence the branch on `contentType`: this returns
     * PLAIN TEXT to quote, whatever artifact the server served.
     */
    const loadMessageText = async (message: Message): Promise<string> => {
        if (!message.id) {
            return '';
        }

        const body = await fetchBody(message.id);
        if (!body) {
            return '';
        }

        if (body.textBody) {
            return body.textBody;
        }

        if (body.htmlBody) {
            return htmlToPlainText(body.htmlBody);
        }

        return '';
    };

    const formatForwardedMessage = (message: Message, text: string): string => {
        return `<p><br></p><hr><p><strong>Forwarded message:</strong></p><p><strong>From:</strong> ${message.fromText || ''}</p><p><strong>Date:</strong> ${message.date || ''}</p><p><strong>Subject:</strong> ${message.subject || ''}</p><p><strong>To:</strong> ${message.toText || ''}</p><p><br></p><p>${text.replace(/\n/g, '<br>')}</p>`;
    };

    const formatReplyMessage = (message: Message, text: string): string => {
        return `<p><br></p><hr><p>On ${message.date || ''}, ${message.fromText || ''} wrote:</p><blockquote>${text.replace(/\n/g, '<br>')}</blockquote>`;
    };

    useEffect(() => {
        if (!opened || (!replyTo && !forwardMessage)) {
            setInitialValues({ aliasId: aliasId || '', to: '', subject: '', htmlBody: '', useHtml: true });
            return;
        }

        const loadMessage = async () => {
            const message = replyTo || forwardMessage;
            if (!message) return;

            // Both branches do the same thing: pull the quoted text, format it,
            // seed the form. A failed body read degrades to an empty quote rather
            // than blocking the compose window — the user can still write.
            const quoted = await loadMessageText(message).catch((err) => {
                console.error('Failed to load quoted message text:', err);
                return '';
            });

            if (forwardMessage) {
                const subject = forwardMessage.subject?.startsWith('Fwd: ')
                    ? forwardMessage.subject
                    : `Fwd: ${forwardMessage.subject || ''}`;

                setInitialValues({
                    aliasId: forwardMessage.aliasId || aliasId || '',
                    to: '',
                    subject,
                    htmlBody: quoted ? formatForwardedMessage(forwardMessage, quoted) : '',
                    useHtml: true,
                });
            } else if (replyTo) {
                const toRecipients = replyAll && replyTo.toText ? replyTo.toText : replyTo.fromText || '';
                const subject = replyTo.subject?.startsWith('Re: ')
                    ? replyTo.subject
                    : `Re: ${replyTo.subject || ''}`;

                setInitialValues({
                    aliasId: replyTo.aliasId || aliasId || '',
                    to: toRecipients,
                    subject,
                    htmlBody: quoted ? formatReplyMessage(replyTo, quoted) : '',
                    useHtml: true,
                });
            }
        };

        loadMessage();
    }, [opened, replyTo, replyAll, forwardMessage, aliasId]);

    const handleSubmit = async (values: ComposeEmailFormValues) => {
        const recipients = values.to.split(',').map(email => email.trim());
        
        await sendEmail({
            aliasId: values.aliasId,
            to: recipients,
            subject: values.subject,
            htmlBody: values.useHtml ? values.htmlBody : undefined,
            textBody: values.useHtml ? htmlToPlainText(values.htmlBody) : values.htmlBody,
            inReplyTo: replyTo?.messageId,
        });
        
        queryClient.invalidateQueries({ queryKey: ['messages'] });
    };

    const getTitle = () => {
        if (forwardMessage) return 'Forward';
        if (replyTo) return replyAll ? 'Reply All' : 'Reply';
        return 'Compose Email';
    };

    return {
        initialValues,
        aliasOptions,
        handleSubmit,
        generateTestMessage,
        title: getTitle(),
    };
};
